import React, {useEffect, useState, useRef, useContext} from 'react';
import {useHistory, useParams} from 'react-router';
import useConfig from 'hooks/use-config';
import SpotUtils from 'utils/spot';
import {Helmet} from 'react-helmet-async';
import {Box, Flex, Heading, Text} from '@spothero/ui';
import isEmpty from 'lodash/isEmpty';
import BadDataPage from './components/bad-data-page/BadDataPage';
import dayjs from 'utils/dayjs-timezone';
import ErrorUtils from 'utils/error-utils';
import TimeUtils from 'utils/time';
import {ReferrerPartners} from 'utils/partner';
import StorageUtils from '@spothero/utils/storage';
import SHLoader from 'common/loader';
import useMobileBreakPoint from 'hooks/use-mobile-breakpoint';
import SpotImages from 'common/spot-details/spot-images';
import AmenitiesList from 'common/spot-details/amenities-list';
import TrustSealValueProps from 'common/spot-details/trust-seal-and-value-props';
import SpotDetailsAccordion from './components/SpotDetailsAccordion';
import UpdateReservationTimes from './components/update-reservation-times';
import FacilityUtils from 'utils/facility-utils';
import {useSelector, useDispatch} from 'react-redux';
import trackFacilityDetailsPage from 'segment/pages/facility-details';
import trackAvailabilityErrorThrown from 'segment/events/availability-error-thrown';
import ThingsYouShouldKnow from 'common/spot-details/things-you-should-know';
import HowToRedeem from 'common/spot-details/redemption-instructions';
import AccessHours from 'common/spot-details/access-hours';
import StarRatingWithCategory from 'common/star-rating-with-category';
import AmenitiesInOutSummary from 'common/spot-details/amenities-inout-summary';
import {trackFacilityDetailsProductsSearched} from 'segment/events/products-searched';
import GettingThere from './components/getting-there';
import SearchTracking, {SEARCH_TRACKING_ACTIONS} from 'utils/search-tracking';
import {CarouselVariant} from 'common/image-carousel';
import {GoogleMapsProvider} from 'common/google-map-context';
import ShuttleInformation from './components/shuttle-information/ShuttleInformation';
import {
    StorageDurations,
    StorageKeys,
    StorageTypes,
} from 'common/constants/storageKeys';
import {push} from 'store/router/router-actions';
import {OptimizelyContext} from '@optimizely/react-sdk';

const leftColumnItemStyleProps = {
    marginTop: 0,
    marginRight: 4,
    marginBottom: 0,
    marginLeft: 4,
    paddingTop: 4,
    paddingRight: 4,
    paddingBottom: 4,
    paddingLeft: 0,
    borderTopWidth: '1px',
    borderTopColor: 'gray.200',
};

const SpotDetailsFacility = () => {
    const user = useSelector(store => store.user.data);
    const searchRequest = useSelector(store => store.searchRequest);
    const isRebookingReservation = useSelector(
        store => store.checkout.isRebookingReservation
    );
    const {spotId} = useParams();
    const {apiDateTimeFormat} = useConfig();
    // We don't want to get a page flash every time we fetch a new rate, so we hold spot and rate as separate pieces of state
    // Even though they actually call the same endpoint. In a future state, may be good for spotData to just call the facility endpoint
    // and to probably do so server-side.
    const [spotData, setSpotData] = useState(null);
    const [rateData, setRateData] = useState(null);
    const [facilityLoading, setFacilityLoading] = useState(true);
    const [isRatesLoading, setIsRatesLoading] = useState(true);
    const [isRatesError, setIsRatesError] = useState(false);
    const [shouldCheckRates, setShouldCheckRates] = useState(false);
    const [starts, setStarts] = useState(null);
    const [ends, setEnds] = useState(null);
    const [isFacilityError, setIsFacilityError] = useState(false);
    const [isAutoExtension, setIsAutoExtension] = useState(false);
    const {
        location: {search},
    } = useHistory();
    const dispatch = useDispatch();
    const isMobile = useMobileBreakPoint('desktop');
    const isInitialSearch = useRef(true);
    const urlParams = new URLSearchParams(search);
    const partner = urlParams.get('partner');
    const rwgToken = urlParams.get('rwg_token');
    const {optimizely} = useContext(OptimizelyContext);

    useEffect(() => {
        if (partner) {
            StorageUtils.set(
                StorageKeys.PARTNER_AFFILIATE,
                partner,
                StorageTypes.SESSION
            );
        }
    }, [partner]);

    const updateStartsAndEnds = ({starts: newStarts, ends: newEnds}) => {
        setIsRatesLoading(true);

        setStarts(newStarts);
        setEnds(newEnds);
        setShouldCheckRates(true);
    };

    // We just want to make the first call to get facility details;
    useEffect(() => {
        const onSpotLoaded = updatedSpotData => {
            const hasAirportRate = updatedSpotData.facility.airport_available;
            const now = dayjs().tz(updatedSpotData.timezone);
            const defaultStarts = hasAirportRate
                ? TimeUtils.calculateDefaultAirportStarts(now)
                : TimeUtils.calculateDefaultStarts(now);
            const defaultEnds = hasAirportRate
                ? TimeUtils.calculateDefaultAirportEnds(defaultStarts)
                : TimeUtils.calculateDefaultEnds(defaultStarts);

            trackFacilityDetailsPage({spot: updatedSpotData});

            setStarts(defaultStarts.format(apiDateTimeFormat));
            setEnds(defaultEnds.format(apiDateTimeFormat));
            setSpotData(updatedSpotData);
            setFacilityLoading(false);
            setShouldCheckRates(true);
        };

        const fetchSpot = async () => {
            setFacilityLoading(true);
            const dummyStarts = dayjs();
            const dummyEnds = TimeUtils.calculateDefaultEnds(dummyStarts);

            await SpotUtils.loadRatesSpotDetail({
                spotId,
                starts: dummyStarts.format(apiDateTimeFormat),
                ends: dummyEnds.format(apiDateTimeFormat),
                searchDataParams: {
                    starts: dummyStarts.format(apiDateTimeFormat),
                    ends: dummyEnds.format(apiDateTimeFormat),
                },
                onSuccessWithRates: updatedSpotData => {
                    onSpotLoaded(updatedSpotData);
                },
                onSuccessWithoutRates: partialSpotData => {
                    onSpotLoaded(partialSpotData);
                },
                onError: error => {
                    // At some point may want to only call this if not a 404
                    ErrorUtils.sendSentryMessage({
                        error,
                        customErrorMessage:
                            'Error Loading Facility Details Page',
                    });
                    console.log('Error loading spot data: ', error); // eslint-disable-line no-console

                    setIsFacilityError(true);
                    setFacilityLoading(false);
                },
            });
        };

        fetchSpot();
    }, [spotId, apiDateTimeFormat]);

    // Want this to run to re-check rates
    useEffect(() => {
        if (!starts || !ends || !spotData?.timezone) {
            return;
        }

        if (!shouldCheckRates) {
            return;
        }

        const checkForAutoExtension = rateResponseData => {
            const rateEnds = rateResponseData?.selectedRate?.ends;

            if (!rateEnds) {
                return;
            }

            const rateEndsObj = dayjs(rateEnds);
            const endObj = dayjs(ends);
            const autoExtensionDifference = rateEndsObj.diff(endObj, 'minute');

            // if it is an auto extension then update reservation time ends
            if (autoExtensionDifference > 0) {
                setEnds(rateEndsObj.format(apiDateTimeFormat));

                if (!isInitialSearch.current) {
                    // if it's not the initial search then display auto extension messaging in UpdateReservationTimes
                    setIsAutoExtension(true);
                }
                setShouldCheckRates(false);
            }
        };

        const hasAirportRate = spotData.facility.airport_available;

        const fetchRateData = async () => {
            setIsRatesLoading(true);
            setRateData(null);
            setIsAutoExtension(false);

            SearchTracking.updateAction(
                isInitialSearch.current
                    ? SEARCH_TRACKING_ACTIONS.SEARCH
                    : SEARCH_TRACKING_ACTIONS.DATE_CHANGED
            );

            await SpotUtils.loadRatesSpotDetail({
                spotId,
                starts,
                ends,
                timezone: spotData.timezone,
                searchDataParams: {
                    starts,
                    ends,
                },
                isAirport: hasAirportRate,
                onSuccessWithRates: updatedRatesData => {
                    if (updatedRatesData?.availability?.available) {
                        setRateData(updatedRatesData);
                        checkForAutoExtension(updatedRatesData);
                        setIsRatesError(false);
                    } else {
                        setIsRatesError(true);
                    }
                    setSpotData(updatedRatesData);
                    setIsRatesLoading(false);
                    setShouldCheckRates(false);
                    trackFacilityDetailsProductsSearched({
                        starts,
                        ends,
                        spot: updatedRatesData,
                    });
                    isInitialSearch.current = false;
                },
                onSuccessWithoutRates: () => {
                    trackAvailabilityErrorThrown({
                        location: 'Facility Details Page',
                        errorFetching: false,
                        spotId,
                        city: spotData?.facility?.city,
                    });

                    trackFacilityDetailsProductsSearched({
                        starts,
                        ends,
                        spot: spotData,
                        noResults: true,
                    });

                    setIsRatesError(true);
                    setIsRatesLoading(false);
                    setShouldCheckRates(false);
                    isInitialSearch.current = false;
                },
                onError: error => {
                    trackAvailabilityErrorThrown({
                        location: 'Facility Details Page',
                        errorFetching: true,
                        spotId,
                        city: spotData?.facility?.city,
                    });

                    trackFacilityDetailsProductsSearched({
                        starts,
                        ends,
                        spot: spotData,
                        noResults: true,
                    });

                    // At some point may want to only call this in certain situations / not at all
                    ErrorUtils.sendSentryMessage({
                        error,
                        customErrorMessage:
                            'Error Loading Facility Details Page',
                    });

                    setIsRatesError(true);
                    setIsRatesLoading(false);
                    setShouldCheckRates(false);
                    isInitialSearch.current = false;
                },
            });
        };

        fetchRateData();
    }, [starts, ends, spotData, spotId, shouldCheckRates, apiDateTimeFormat]);

    const facilityAvailable = spotData?.facility?.status
        ? spotData.facility.status === FacilityUtils.STATUSES.ON ||
          spotData.facility.status ===
              FacilityUtils.STATUSES.ON_SALES_NOT_ALLOWED ||
          spotData.facility.status ===
              FacilityUtils.STATUSES.ON_DIRECT_LINK_ONLY ||
          (user.isAdmin &&
              spotData.facility.status ===
                  FacilityUtils.STATUSES.OFF_ADMIN_VIEWABLE)
        : true;

    useEffect(() => {
        if (
            rwgToken &&
            partner === ReferrerPartners.GOOGLE &&
            !isFacilityError
        ) {
            // set cookie for Google Maps attribution
            StorageUtils.set(
                StorageKeys.RWG_COOKIE,
                rwgToken,
                StorageTypes.COOKIE,
                StorageDurations.MONTH
            );
        }
    }, [rwgToken, partner, isFacilityError]);

    if (isFacilityError || !facilityAvailable) {
        return <BadDataPage />;
    }

    if (facilityLoading || !spotData) {
        return <SHLoader />;
    }

    /* eslint-disable camelcase */
    const {title, facility, selectedRate, airportCode, shuttle} = spotData;
    const {
        images: facilityImages,
        getting_here: gettingHere,
        cancellation_allowed: cancellationAllowed,
        amenities_full: nonRateAmenities,
        restrictions: facilityRestrictions,
        redemption_instructions: facilityInstructions,
        redemption_instructions_arrival: redemptionInstructionsArrival,
        redemption_instructions_departure: redemptionInstructionsDeparture,
        post_purchase_instructions: postPurchaseInstructions,
        access_hours_formatted: facilityAccessHours,
        rating_info: {
            star_rating: averageRating,
            number_of_ratings: numberOfReviews,
        },
    } = facility;

    /* eslint-enable camelcase */
    const searchUrl = airportCode
        ? `/search?kind=airport&id=${airportCode}&airport=true&starts=${starts}&ends=${ends}&filterSpot=${spotId}`
        : `/search?latitude=${facility.latitude}&longitude=${facility.longitude}&starts=${starts}&ends=${ends}&filterSpot=${spotId}`;

    const handleCheckoutClick = () => {
        const checkoutUrl = airportCode
            ? `${rateData?.selectedRate?.fullUrl}&airport=true`
            : rateData?.selectedRate?.fullUrl;

        const shouldSend = SpotUtils.shouldSendToExpressCheckout({
            spotData: rateData,
            searchRequest,
            isRebookingReservation,
            isAdmin: user.isAdmin,
            optimizely,
            isMobile,
        });

        if (shouldSend) {
            window.location.href = rateData.selectedRate.expressCheckoutUrl;
        } else {
            dispatch(push(checkoutUrl));
        }
    };
    const rateAmenities = selectedRate?.amenities || [];
    const hasInOutAmenity = Boolean(
        rateAmenities.find(amenity => amenity.slug === 'in-out')
    );
    const showAccessHours = Boolean(
        selectedRate?.access_hours_formatted ||
            selectedRate?.display_hours_comment ||
            !isEmpty(facilityAccessHours)
    );

    const renderSpotInfo = () => {
        return (
            <Box marginX={isMobile ? 0 : 4} marginBottom={4}>
                <SpotImages
                    carouselVariant={
                        isMobile
                            ? CarouselVariant.SINGLE
                            : CarouselVariant.MEDIUM
                    }
                    images={facilityImages}
                    alt={title}
                    carouselHeight={isMobile ? 16 : 20}
                />
                <Heading
                    as="h3"
                    marginBottom={2}
                    marginTop={4}
                    marginX={isMobile ? 4 : 0}
                >
                    {title}
                </Heading>
                <Flex
                    alignItems="end"
                    gap={numberOfReviews > 0 ? 3 : 0}
                    marginX={isMobile ? 4 : 0}
                >
                    <StarRatingWithCategory
                        numberOfReviews={numberOfReviews}
                        starRating={averageRating}
                    />
                    <AmenitiesInOutSummary hasInOutAmenity={hasInOutAmenity} />
                </Flex>
            </Box>
        );
    };

    return (
        <GoogleMapsProvider>
            <Box
                sx={{
                    scrollMarginTop: {
                        base: '4rem',
                        tablet: 0,
                    },
                    '.SpotDetails-section': {
                        marginX: {base: '0', desktop: 4},
                        padding: 4,
                        borderColor: 'gray.100',
                    },
                    '.SpotDetails-section:first-of-type': {
                        marginTop: {base: 0, tablet: 2},
                        borderTopWidth: {base: 0, tablet: '1px'},
                    },
                }}
                as="main"
                paddingX={{base: 0, tablet: 10}}
                data-testid="SpotDetailsFacility"
            >
                <Helmet>
                    <meta name="robots" content="noindex, nofollow" />
                    <body className="standalone-page" />
                </Helmet>
                <Flex
                    flexDirection={{base: 'column', desktop: 'row-reverse'}}
                    alignItems={{base: 'center', desktop: 'stretch'}}
                    width="100%"
                    marginX="auto"
                    maxWidth={{
                        base: 'none',
                        tablet: '37.5rem',
                        desktop: '75rem',
                    }}
                    marginTop={isMobile ? 0 : 10}
                    justifyContent={{base: 'auto', desktop: 'space-between'}}
                >
                    <Flex
                        flexDirection="column"
                        width={{base: '100%', desktop: '25rem'}}
                        alignItems="center"
                    >
                        {isMobile && renderSpotInfo()}
                        <UpdateReservationTimes
                            isAdmin={user.isAdmin}
                            searchUrl={searchUrl}
                            onCheckoutClick={handleCheckoutClick}
                            isRatesSuccess={Boolean(rateData)}
                            isRatesLoading={isRatesLoading}
                            isRatesError={isRatesError}
                            isAutoExtension={isAutoExtension}
                            price={rateData?.selectedRate?.price}
                            currencyType={rateData?.selectedRate?.currency_type}
                            ends={ends}
                            starts={starts}
                            timezone={spotData.timezone}
                            updateStartsAndEnds={updateStartsAndEnds}
                            spotId={spotData.spotId}
                            priceBreakdown={
                                rateData?.selectedRate?.price_breakdown
                            }
                        />
                    </Flex>
                    <Flex
                        flexDirection="column"
                        width="100%"
                        flex={{base: null, desktop: 1}}
                        marginRight={{base: 0, desktop: 8}}
                        maxWidth={{base: 'none', desktop: '40.5rem'}}
                    >
                        {!isMobile && renderSpotInfo()}
                        <ThingsYouShouldKnow
                            hasInOutAmenity={hasInOutAmenity}
                            restrictions={facilityRestrictions}
                        />
                        {Boolean(shuttle) && (
                            <ShuttleInformation
                                shuttle={shuttle}
                                facilityTitle={title}
                                iataCode={airportCode}
                            />
                        )}
                        <AmenitiesList
                            rate={
                                rateData?.selectedRate
                                    ? rateData.selectedRate
                                    : spotData.selectedRate
                            }
                            nonRateAmenities={nonRateAmenities}
                            isMonthly={false}
                        />
                        {showAccessHours && (
                            <AccessHours
                                rate={selectedRate}
                                facility={facility}
                                sectionTitle="Access Hours"
                            />
                        )}
                        <GettingThere
                            spotLatitude={spotData.latitude}
                            spotLongitude={spotData.longitude}
                            instructions={gettingHere}
                        />
                        {redemptionInstructionsArrival && (
                            <HowToRedeem
                                sectionTitle="Arrival"
                                instructions={redemptionInstructionsArrival}
                            />
                        )}
                        {redemptionInstructionsDeparture && (
                            <HowToRedeem
                                sectionTitle="Departure"
                                instructions={redemptionInstructionsDeparture}
                            />
                        )}
                        {!redemptionInstructionsArrival &&
                            (facilityInstructions ? (
                                <HowToRedeem
                                    sectionTitle="How to Redeem"
                                    instructions={facilityInstructions}
                                />
                            ) : (
                                <Box {...leftColumnItemStyleProps}>
                                    <Heading as="h3" fontSize="base">
                                        How to Redeem
                                    </Heading>
                                    <Text
                                        as="span"
                                        dangerouslySetInnerHTML={{
                                            __html: postPurchaseInstructions,
                                        }}
                                    />
                                </Box>
                            ))}
                        <SpotDetailsAccordion
                            cancellationAllowed={cancellationAllowed}
                        />

                        <Box marginY={4} maxWidth={96}>
                            <TrustSealValueProps />
                        </Box>
                    </Flex>
                </Flex>
            </Box>
        </GoogleMapsProvider>
    );
};

export default SpotDetailsFacility;
